/**
 * Procedural Confirm & Cancel Sound Generators
 * 
 * Confirm: Ascending major chord arpeggio (positive feedback)
 * Cancel: Descending tone (negative/neutral feedback)
 */

import { PROCEDURAL_PARAMS } from '../audioConfig.js';

/**
 * Create and play a confirm sound (ascending arpeggio)
 * @param {AudioContext} ctx - Web Audio context
 * @param {GainNode} destination - Destination gain node
 */
export function createConfirmSound(ctx, destination) {
  if (!ctx || !destination) return;

  const params = PROCEDURAL_PARAMS.confirm;
  const now = ctx.currentTime;

  params.frequencies.forEach((freq, index) => {
    const startTime = now + (index * params.stagger);

    const oscillator = ctx.createOscillator();
    oscillator.type = params.type;
    oscillator.frequency.setValueAtTime(freq, startTime);

    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(0.2, startTime + 0.01);
    gainNode.gain.setValueAtTime(0.2, startTime + params.duration * 0.3);
    gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + params.duration);

    oscillator.connect(gainNode);
    gainNode.connect(destination);

    oscillator.start(startTime);
    oscillator.stop(startTime + params.duration + 0.01);

    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
    };
  });
}

/**
 * Create and play a cancel sound (descending tone)
 * @param {AudioContext} ctx - Web Audio context
 * @param {GainNode} destination - Destination gain node
 */
export function createCancelSound(ctx, destination) {
  if (!ctx || !destination) return;

  const params = PROCEDURAL_PARAMS.cancel;
  const now = ctx.currentTime;

  // Main descending tone
  const oscillator = ctx.createOscillator();
  oscillator.type = params.type;
  oscillator.frequency.setValueAtTime(params.frequency, now);
  oscillator.frequency.exponentialRampToValueAtTime(
    params.endFrequency,
    now + params.duration
  );

  // Envelope
  const gainNode = ctx.createGain();
  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(0.2, now + 0.01);
  gainNode.gain.exponentialRampToValueAtTime(0.001, now + params.duration);

  // Soft filter
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(1500, now);
  filter.frequency.linearRampToValueAtTime(500, now + params.duration);

  oscillator.connect(filter);
  filter.connect(gainNode);
  gainNode.connect(destination);

  oscillator.start(now);
  oscillator.stop(now + params.duration + 0.01);

  oscillator.onended = () => {
    oscillator.disconnect();
    filter.disconnect();
    gainNode.disconnect();
  };
}

/**
 * Create a success confirmation (more elaborate)
 */
export function createSuccessSound(ctx, destination) {
  if (!ctx || !destination) return;

  const now = ctx.currentTime;
  const frequencies = [523.25, 659.25, 783.99, 1046.5]; // C5 major + octave

  frequencies.forEach((freq, index) => {
    const startTime = now + (index * 0.04);
    const duration = 0.15 - (index * 0.02);

    const oscillator = ctx.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(freq, startTime);

    const gainNode = ctx.createGain();
    const volume = 0.15 - (index * 0.02);
    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.008);
    gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

    oscillator.connect(gainNode);
    gainNode.connect(destination);

    oscillator.start(startTime);
    oscillator.stop(startTime + duration + 0.01);

    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
    };
  });
}
